import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createMember, getSpiritualFathers, getOrdainedAuthorities, getNationalities } from '../../services/api';
import { mergeNationalities } from '../../data/nationalities';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import Toast from '../../components/Toast';
import PhoneInput from '../../components/PhoneInput';

const currentYear = new Date().getFullYear();
const calcAge = (y) => y && !isNaN(y) ? currentYear - parseInt(y) : null;

// `country` defaults to Zambia — dependents living abroad are excluded from
// the local demographics counts (Men/Women/Boys/Girls).
const emptyDependent = { first_name: '', middle_name: '', last_name: '', relationship: 'wife', birth_year: '', holy_communion: 'unknown', country: 'Zambia' };

const AddMember = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFatherId = searchParams.get('father_id') || '';

  const [form, setForm] = useState({
    title: '', first_name: '', middle_name: '', last_name: '', christian_name: '',
    gender: '', birth_year: '', marital_status: '', occupation: '', phone: '', whatsapp: '', email: '', address: '',
    ordination_date: '', ordained_by: '', nationality: '',
    holy_communion: 'unknown',
    membership_status: 'member',
    is_spiritual_father: false, spiritual_father_id: preselectedFatherId,
  });
  const [dependents, setDependents] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [showUnassignedWarning, setShowUnassignedWarning] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const doSave = async () => {
    setSaving(true);
    try {
      await createMember({ ...form, dependents });
      showToast('Member registered successfully');
      setTimeout(() => navigate('/members'), 1200);
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const [authorities, setAuthorities] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  useEffect(() => {
    getSpiritualFathers().then(r => setLeaders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getOrdainedAuthorities().then(r => setAuthorities(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getNationalities().then(r => setNationalities(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const titleToGender = { 'Mr.': 'male', 'Mrs.': 'female', 'Priest': 'male', 'Deacon': 'male' };
  const setTitle = (e) => {
    const title = e.target.value;
    setForm(p => ({ ...p, title, gender: titleToGender[title] || p.gender }));
  };

  const addDependent = () => setDependents(p => [...p, { ...emptyDependent }]);
  const removeDependent = (i) => setDependents(p => p.filter((_, idx) => idx !== i));
  const setDep = (i, field) => e => setDependents(p => {
    const u = [...p];
    u[i] = { ...u[i], [field]: e.target.value };
    // Auto-fill middle and last name for son/daughter
    if (field === 'relationship') {
      const rel = e.target.value;
      if (rel === 'son' || rel === 'daughter') {
        if (form.gender === 'female') {
          // Kids inherit names from father (husband), not mother
          const husband = u.find((d, idx) => idx !== i && d.relationship === 'husband');
          if (husband) {
            u[i].middle_name = husband.first_name || '';
            u[i].last_name  = husband.middle_name || '';
          } else {
            u[i].middle_name = '';
            u[i].last_name  = '';
          }
        } else {
          u[i].middle_name = form.first_name || '';
          u[i].last_name  = form.middle_name || '';
        }
      } else {
        u[i].middle_name = '';
        u[i].last_name  = '';
      }
    }
    return u;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title)            return setError('Title is required');
    if (!form.first_name)       return setError('First name is required');
    if (!form.middle_name)      return setError('Middle name is required');
    if (!form.last_name)        return setError('Last name is required');
    if (!form.christian_name)   return setError('Christian name is required');
    if (!form.gender)           return setError('Gender is required');
    if (!form.birth_year)       return setError('Year of birth is required');
    if (!form.marital_status)   return setError('Marital status is required');
    if (!form.occupation)       return setError('Occupation is required');
    if (!form.phone)            return setError('Phone is required');
    if (!form.address)          return setError('Address is required');
    if (!form.holy_communion || form.holy_communion === 'unknown') return setError('Holy Communion status is required');

    for (let i = 0; i < dependents.length; i++) {
      const d = dependents[i];
      if (!d.first_name) return setError(`Dependent ${i + 1}: first name is required`);
      if (!d.birth_year) return setError(`Dependent ${i + 1}: birth year is required`);
      if (!d.holy_communion || d.holy_communion === 'unknown') return setError(`Dependent ${i + 1}: Holy Communion status is required`);
      if ((d.relationship === 'son' || d.relationship === 'daughter')) {
        if (!d.middle_name) return setError(`Dependent ${i + 1}: middle name (father's first name) is required`);
        if (!d.last_name)   return setError(`Dependent ${i + 1}: last name (father's middle name) is required`);
      }
    }

    if (!form.spiritual_father_id && !form.is_spiritual_father) {
      setShowUnassignedWarning(true);
      return;
    }
    await doSave();
  };

  const preselectedLeader = leaders.find(l => String(l.id) === String(preselectedFatherId));

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('members.registerMember')}</h1></div>
        <button className="btn btn-secondary" onClick={() => navigate('/members')}>← {t('common.back')}</button>
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Card number preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#4f46e5', fontWeight: 600 }}>Membership Card No.</span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Will be auto-generated on save (e.g. MCN-0014)</span>
        </div>

        {/* Personal Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">{t('members.personalInfo')}</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label>{t('members.title')} <span className="required">*</span></label>
                <select value={form.title} onChange={setTitle}>
                  <option value="">— {t('common.search')} —</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Priest">Priest</option>
                  <option value="Deacon">Deacon</option>
                </select>
              </div>
              {(form.title === 'Priest' || form.title === 'Deacon') && (
                <>
                  <div className="form-group">
                    <label>Date of Ordination</label>
                    <input type="date" value={form.ordination_date} onChange={set('ordination_date')} />
                  </div>
                  <div className="form-group">
                    <label>Ordained By</label>
                    <input
                      list="ordained-by-list"
                      value={form.ordained_by}
                      onChange={set('ordained_by')}
                      placeholder="Bishop / authority who ordained"
                    />
                    <datalist id="ordained-by-list">
                      {authorities.map(a => <option key={a} value={a} />)}
                    </datalist>
                  </div>
                </>
              )}
              <div className="form-group">
                <label>{t('members.firstName')} <span className="required">*</span></label>
                <input value={form.first_name} onChange={set('first_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.middleName')} <span className="required">*</span></label>
                <input value={form.middle_name} onChange={set('middle_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.lastName')} <span className="required">*</span></label>
                <input value={form.last_name} onChange={set('last_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.christianName')} <span className="required">*</span></label>
                <input value={form.christian_name} onChange={set('christian_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.gender')} <span className="required">*</span></label>
                <select value={form.gender} onChange={set('gender')}>
                  <option value="">—</option>
                  <option value="male">{t('members.male')}</option>
                  <option value="female">{t('members.female')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  {t('members.yearOfBirth')} <span className="required">*</span>
                  {form.birth_year && !isNaN(form.birth_year) && (
                    <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 8 }}>→ {t('members.age')}: {currentYear - parseInt(form.birth_year)}</span>
                  )}
                </label>
                <input type="number" value={form.birth_year} onChange={set('birth_year')} min="1900" max={currentYear} />
              </div>
              <div className="form-group">
                <label>{t('members.maritalStatus')} <span className="required">*</span></label>
                <select value={form.marital_status} onChange={set('marital_status')}>
                  <option value="">—</option>
                  <option value="Single">{t('members.single')}</option>
                  <option value="Married">{t('members.married')}</option>
                  <option value="Widowed">{t('members.widowed')}</option>
                  <option value="Divorced">{t('members.divorced')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('members.occupation')} <span className="required">*</span></label>
                <input value={form.occupation} onChange={set('occupation')} />
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <input
                  list="nationality-list"
                  value={form.nationality}
                  onChange={set('nationality')}
                  placeholder="e.g. Zambian, Ethiopian"
                />
                <datalist id="nationality-list">
                  {mergeNationalities(nationalities).map(n => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>{t('common.phone')} <span className="required">*</span></label>
                <PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <PhoneInput value={form.whatsapp} onChange={v => setForm(p => ({ ...p, whatsapp: v }))} />
              </div>
              <div className="form-group">
                <label>{t('common.email')}</label>
                <input type="email" value={form.email} onChange={set('email')} />
              </div>
              <div className="form-group">
                <label>{t('members.holyCommunion')} <span className="required">*</span></label>
                <select value={form.holy_communion} onChange={set('holy_communion')}>
                  <option value="unknown">—</option>
                  <option value="taking">{t('members.taking')}</option>
                  <option value="not_taking">{t('members.notTaking')}</option>
                  <option value="excommunicated">{t('members.excommunicated')}</option>
                </select>
              </div>
              <div className="form-group form-full">
                <label>{t('common.address')} <span className="required">*</span></label>
                <input value={form.address} onChange={set('address')} />
              </div>
            </div>
          </div>
        </div>

        {/* Spiritual Assignment */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">{t('nav.spiritualCare')}</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group form-full">
                <label>{t('members.spiritualFather')}</label>
                {preselectedLeader && preselectedFatherId && !form.is_spiritual_father ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8 }}>
                    <span style={{ fontWeight: 600, color: '#4f46e5' }}>{preselectedLeader.first_name} {preselectedLeader.last_name}</span>
                    <button type="button" className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => setForm(p => ({ ...p, spiritual_father_id: '' }))}>Change</button>
                  </div>
                ) : (
                  <select id="spiritual-father-select" value={form.spiritual_father_id} onChange={set('spiritual_father_id')} disabled={form.is_spiritual_father}>
                    <option value="">— {t('members.unassigned')} —</option>
                    {leaders.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
                  </select>
                )}
                {form.is_spiritual_father && <small style={{ color: '#6b7280' }}>Spiritual fathers are not assigned to another leader.</small>}
              </div>
              <div className="form-group form-full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: form.is_spiritual_father ? '#eef2ff' : '#f8fafc', border: `1px solid ${form.is_spiritual_father ? '#a5b4fc' : '#e5e7eb'}`, borderRadius: 8, padding: '10px 14px' }}>
                  <input type="checkbox" checked={form.is_spiritual_father} onChange={e => setForm(p => ({ ...p, is_spiritual_father: e.target.checked, spiritual_father_id: e.target.checked ? '' : p.spiritual_father_id }))} style={{ width: 'auto', padding: 0, accentColor: '#4f46e5' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: form.is_spiritual_father ? '#4f46e5' : '#374151' }}>{t('members.registerAsLeader')}</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Dependents */}
        {!form.is_spiritual_father && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('members.dependents')}</span>
              <button type="button" className="btn btn-sm btn-secondary" onClick={addDependent}><FiPlus /> {t('members.addDependent')}</button>
            </div>
            <div className="card-body">
              {dependents.length === 0 ? (
                <p style={{ fontSize: 13, color: '#9ca3af' }}>No dependents added. Click "Add Dependent" to add wife, children, or others.</p>
              ) : dependents.map((d, i) => {
                const age = calcAge(d.birth_year);
                const isChild = d.relationship === 'son' || d.relationship === 'daughter';
                const husbandPresent = dependents.some((x, idx) => idx !== i && x.relationship === 'husband' && x.first_name);
                const needsManualEntry = isChild && form.gender === 'female' && !husbandPresent;
                return (
                  <div key={i} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <strong style={{ fontSize: 13 }}>Dependent {i + 1}</strong>
                      <button type="button" className="btn-icon danger" onClick={() => removeDependent(i)}><FiTrash2 /></button>
                    </div>
                    {needsManualEntry && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#92400e' }}>
                        ⚠ Add the husband as a dependent first to auto-fill the child's name, or type the father's <strong>first name</strong> into Middle Name and the father's <strong>middle name</strong> into Last Name.
                      </div>
                    )}
                    <div className="form-grid">
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Relationship <span className="required">*</span></label>
                        <select value={d.relationship} onChange={setDep(i, 'relationship')}>
                          {form.gender !== 'female' && <option value="wife">Wife</option>}
                          {form.gender !== 'male'   && <option value="husband">Husband</option>}
                          <option value="son">Son</option>
                          <option value="daughter">Daughter</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>
                          Birth Year <span className="required">*</span>
                          {age !== null && <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 8 }}>→ Age: {age}</span>}
                        </label>
                        <input type="number" value={d.birth_year} onChange={setDep(i, 'birth_year')} placeholder={`e.g. ${currentYear - 10}`} min="1900" max={currentYear} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>First Name <span className="required">*</span></label>
                        <input value={d.first_name} onChange={setDep(i, 'first_name')} placeholder="First name" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>
                          Middle Name {isChild && <span className="required">*</span>}
                          {isChild && d.middle_name &&
                            <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 6, fontSize: 11 }}>auto-filled</span>}
                        </label>
                        <input
                          value={d.middle_name}
                          onChange={setDep(i, 'middle_name')}
                          placeholder={isChild ? `e.g. ${(form.gender === 'female' ? (dependents.find(x => x.relationship === 'husband')?.first_name) : form.first_name) || "father's first name"}` : 'Middle name'}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>
                          Last Name {isChild && <span className="required">*</span>}
                          {isChild && d.last_name &&
                            <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 6, fontSize: 11 }}>auto-filled</span>}
                        </label>
                        <input
                          value={d.last_name}
                          onChange={setDep(i, 'last_name')}
                          placeholder={isChild ? `e.g. ${(form.gender === 'female' ? (dependents.find(x => x.relationship === 'husband')?.middle_name) : form.middle_name) || "father's middle name"}` : 'Last name'}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Holy Communion <span className="required">*</span></label>
                        <select value={d.holy_communion} onChange={setDep(i, 'holy_communion')}>
                          <option value="unknown">— Select —</option>
                          <option value="taking">Taking Communion</option>
                          <option value="not_taking">Not Taking</option>
                          <option value="excommunicated">Excommunicated</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>
                          Where they live
                          {d.country && d.country !== 'Zambia' && (
                            <span style={{ fontWeight: 400, color: '#b91c1c', marginLeft: 6, fontSize: 11 }}>
                              (excluded from demographics)
                            </span>
                          )}
                        </label>
                        <select value={d.country === 'Zambia' || !d.country ? 'Zambia' : 'Other'} onChange={(e) => {
                          // Switching to 'Other' clears the value so the text box opens; switching
                          // back to 'Zambia' resets to the default so demographics counts include them.
                          const v = e.target.value;
                          setDependents(p => { const u = [...p]; u[i] = { ...u[i], country: v === 'Zambia' ? 'Zambia' : '' }; return u; });
                        }}>
                          <option value="Zambia">Zambia</option>
                          <option value="Other">Other (abroad)</option>
                        </select>
                        {d.country !== 'Zambia' && (
                          <input
                            style={{ marginTop: 6 }}
                            value={d.country || ''}
                            onChange={setDep(i, 'country')}
                            placeholder="Country name (e.g. UK, USA)"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('common.saving') : t('members.registerMember')}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/members')}>{t('common.cancel')}</button>
        </div>
      </form>

      {/* Unassigned spiritual father warning */}
      {showUnassignedWarning && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header" style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>⚠️</span>
                <div>
                  <h3 style={{ color: '#92400e' }}>No Spiritual Father Assigned</h3>
                  <p style={{ color: '#a16207', fontSize: 13 }}>This member has no spiritual father yet</p>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
                Do you want the <strong>Admin to assign a spiritual father later</strong>, or do you want to <strong>assign one now</strong>?
              </p>
            </div>
            <div className="modal-footer" style={{ flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setShowUnassignedWarning(false); doSave(); }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Let Admin assign later — save anyway'}
              </button>
              <button
                className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setShowUnassignedWarning(false); document.getElementById('spiritual-father-select')?.scrollIntoView({ behavior: 'smooth' }); }}
              >
                I will assign now — go back
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default AddMember;
